import { TextFieldProps } from '@mui/material';
import { FC, forwardRef, ReactNode, useCallback, useRef } from 'react';

import { FormTextFieldStyled, FormTextFieldStyledProps } from './FormTextField.styled';
import { InputEndAdornment, InputEndAdornmentProps } from './InputEndAdornment';

export type FormTextFieldProps = TextFieldProps & FormTextFieldStyledProps & InputEndAdornmentProps;

export const FormTextField: FC<
  FormTextFieldProps & {
    showEndAdornment?: boolean;
    startAdornment?: ReactNode;
    endAdornment?: ReactNode;
  }
> = forwardRef(
  (
    {
      inputRef: propInputRef,
      hideLabel,
      dropdown,
      canClear,
      onClear,
      InputProps,
      required,
      showEndAdornment = true,
      startAdornment,
      ...props
    },
    ref,
  ) => {
    const localRef = useRef<HTMLInputElement>();
    const inputRef = propInputRef && typeof propInputRef !== 'function' ? propInputRef : localRef;
    const value = props.value ?? props.inputProps?.value;

    const hasValue = props.type === 'number' ? typeof value === 'number' : !!value;

    const handleClear = useCallback(() => {
      if (!inputRef.current) {
        return;
      }

      inputRef.current.value = '';

      props.onChange?.({
        target: inputRef.current,
        nativeEvent: new Event('change'),
      } as React.ChangeEvent<HTMLInputElement>);

      onClear?.();
      // biome-ignore lint/correctness/useExhaustiveDependencies: Legacy code
    }, [onClear, props, inputRef]);

    return (
      <FormTextFieldStyled
        ref={ref}
        inputRef={
          typeof propInputRef === 'function'
            ? (_ref) => {
                localRef.current = _ref;
                propInputRef(localRef.current);
              }
            : inputRef
        }
        InputProps={{
          ...InputProps,
          startAdornment: startAdornment || InputProps?.startAdornment,
          ...(showEndAdornment && {
            endAdornment: InputProps?.endAdornment ?? (
              <InputEndAdornment
                hideLabel={hideLabel ?? hasValue}
                canClear={canClear && hasValue}
                onClear={handleClear}
                required={required}
                dropdown={dropdown}
                hasValue={hasValue}
                {...props}
              />
            ),
          }),
        }}
        {...props}
      />
    );
  },
);
